'use client';

import { ChangeEvent, FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ArticleCard } from '@telialt/core-cms-content/src/components/ArticleCard/ArticleCard';
import { transformArticleCard } from '@telialt/core-cms-content/src/components/ArticleCard/transformers';
import { CardsList } from '@telialt/core-cms-content/src/components/CardsList/CardsList';
import { BlogArticleType } from '@telialt/core-cms-content/src/models/Content';

import { BLOG_CATEGORY_SORT_VALUES } from '../../constants';
import { NotFoundContent } from '../Common/NotFoundContent/NotFoundContent';
import { ArticleListContainer, SortingOptionsContainer, StyledSelect } from './ContentBlogCategoryPage.styles';
import { getNextArticlesUrl } from './utils';

type Props = {
    pageTitle?: string;
    blogArticles: BlogArticleType[];
    totalItems: number;
    currentPage: number;
    pageSize: number;
    sort?: string;
    fullPagePath: string;
};

export const ArticleList: FC<Props> = ({
    pageTitle,
    blogArticles,
    totalItems,
    currentPage,
    pageSize,
    sort,
    fullPagePath,
}) => {
    const { t } = useTranslations();

    const transformedBlogArticles = blogArticles.map(transformArticleCard);

    const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
        window.location.href = getNextArticlesUrl({
            path: fullPagePath,
            currentPage,
            pageSize,
            sort: event.target.value,
        });
    };

    if (!totalItems) {
        return (
            <ArticleListContainer $withTopPadding={true}>
                <NotFoundContent
                    title={t['blog.articles_not_found.title']}
                    description={t['blog.articles_not_found.for_category']}
                />
            </ArticleListContainer>
        );
    }

    return (
        <ArticleListContainer>
            <SortingOptionsContainer>
                <StyledSelect
                    value={sort || BLOG_CATEGORY_SORT_VALUES.NEWEST}
                    onChange={handleSortChange}
                    aria-label={t['blog.article_cards_list.sort.label']}
                    options={[
                        { label: t['blog.article_cards_list.sort.newest'], value: BLOG_CATEGORY_SORT_VALUES.NEWEST },
                        { label: t['blog.article_cards_list.sort.oldest'], value: BLOG_CATEGORY_SORT_VALUES.OLDEST },
                    ]}
                />
            </SortingOptionsContainer>

            <CardsList
                listItems={transformedBlogArticles.map((article) => (
                    <ArticleCard fullHeight={true} variant="secondary" key={article.id} content={article} />
                ))}
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                hrefGetter={({ pageSize: newPageSize, page: newCurrentPage }) => {
                    return getNextArticlesUrl({
                        path: fullPagePath,
                        currentPage: newCurrentPage,
                        pageSize: newPageSize,
                        sort,
                    });
                }}
                data-section="Blog Cards List"
                data-section-name={pageTitle}
            />
        </ArticleListContainer>
    );
};
